import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, TextField, Typography,
} from '@mui/material';
import { fetchGabbaiOrders, upsertGabbaiOrder } from '../gabbaiPortalApi';
import { fieldSxFromTheme, getGabbaiTheme } from '../gabbaiPortalTheme';
import PortalSearchSelect from '../PortalSearchSelect';

export default function OrdersScreen({ activeKolelId, kolels, meta }) {
  const t = getGabbaiTheme();
  const fieldSx = fieldSxFromTheme(t);
  const openExams = meta?.openExams || [];
  const examCities = meta?.examCities || [];

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [examId, setExamId] = useState('');
  const [location, setLocation] = useState(examCities[0] || '');
  const [quantity, setQuantity] = useState('');
  const [kolelId, setKolelId] = useState(activeKolelId || kolels[0]?.id || '');

  const kolelOptions = useMemo(
    () => (kolels || []).map((k) => ({ id: k.id, label: k.name })),
    [kolels]
  );
  const examOptions = useMemo(
    () => openExams.map((ex) => ({
      id: ex.id,
      label: `${ex.code ? `${ex.code} · ` : ''}${ex.name} ${ex.subject || ''}`.trim(),
    })),
    [openExams]
  );
  const cityOptions = useMemo(
    () => examCities.map((city) => ({ id: city, label: city })),
    [examCities]
  );

  useEffect(() => {
    if (activeKolelId) setKolelId(activeKolelId);
  }, [activeKolelId]);

  useEffect(() => {
    if (!location && examCities[0]) setLocation(examCities[0]);
  }, [examCities, location]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (activeKolelId) params.kolelId = activeKolelId;
      const data = await fetchGabbaiOrders(params);
      setOrders(data.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בטעינת ההזמנות');
    } finally {
      setLoading(false);
    }
  }, [activeKolelId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!examId || !location || quantity === '' || !kolelId) {
      setError('יש למלא מבחן, עיר וכמות');
      return;
    }
    setSaving(true);
    try {
      await upsertGabbaiOrder({
        kolelId,
        examId,
        location,
        quantity: Number(quantity),
      });
      setSuccess('ההזמנה נשמרה');
      setQuantity('');
      await load();
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בשמירת ההזמנה');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 640, mx: 'auto' }}>
      <Typography sx={{ color: 'rgba(245,240,230,0.7)', mb: 2, fontSize: '0.9rem' }}>
        הכמות היא מספר השאלונים לכולל בעיר שנבחרה.
      </Typography>

      <Box
        component="form"
        onSubmit={handleSave}
        sx={{
          border: `1px solid ${t.cardBorder}`,
          background: t.panelBg,
          borderRadius: 2.5,
          p: 2,
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Typography sx={{ color: '#f5f0e6', fontWeight: 700, mb: 0.5 }}>
          הזמנה חדשה / עדכון
        </Typography>

        {kolels.length > 1 && (
          <PortalSearchSelect
            label="כולל"
            options={kolelOptions}
            value={kolelId}
            onChange={setKolelId}
            placeholder="חיפוש כולל..."
          />
        )}

        <PortalSearchSelect
          label="מבחן"
          options={examOptions}
          value={examId}
          onChange={setExamId}
          placeholder="חיפוש מבחן..."
        />

        <PortalSearchSelect
          label="עיר מיקום"
          options={cityOptions}
          value={location}
          onChange={setLocation}
          placeholder="חיפוש עיר..."
        />

        <TextField
          fullWidth
          type="tel"
          label="כמות"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value.replace(/\D/g, ''))}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', dir: 'ltr' }}
          sx={fieldSx}
        />

        <Button
          type="submit"
          disabled={saving || !openExams.length}
          sx={{
            mt: 1,
            minHeight: 46,
            fontWeight: 700,
            borderRadius: 2,
            color: t.ctaColor,
            background: t.ctaBg,
            '&:hover': { filter: 'brightness(1.06)' },
            '&.Mui-disabled': { background: t.ctaDisabledBg, color: t.ctaDisabledColor },
          }}
        >
          {saving ? <CircularProgress size={22} sx={{ color: t.ctaColor }} /> : 'שמירת הזמנה'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {loading && (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 4 }}>
          <CircularProgress sx={{ color: t.accent }} />
        </Box>
      )}

      {!loading && !orders.length && (
        <Alert severity="info" sx={{ borderRadius: 2 }}>אין הזמנות עדיין</Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {orders.map((o) => (
          <Box
            key={o.id}
            sx={{
              border: `1px solid ${t.cardBorder}`,
              background: t.cardBg,
              borderRadius: 2.5,
              px: 2,
              py: 1.5,
            }}
          >
            <Typography sx={{ color: '#f5f0e6', fontWeight: 700 }}>
              {o.examCode ? `${o.examCode} · ` : ''}{o.examName}
            </Typography>
            <Typography sx={{ color: 'rgba(245,240,230,0.65)', fontSize: '0.9rem' }}>
              {o.location} · כמות {o.quantity}
              {o.kolelName ? ` · ${o.kolelName}` : ''}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}
